import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { Rocket } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AutoplayVariation } from '@/store/videoFeatures/types/autoplay-ab-test';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface AutoplayABConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variations: AutoplayVariation[];
  distribution: number[];
}

export function AutoplayABConfirmModal({
  open,
  onClose,
  onConfirm,
  variations,
  distribution
}: AutoplayABConfirmModalProps) {
  useLanguage();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ab_test', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para criar testes A/B"));
      onClose();
    }
  }, [open, canEdit, onClose]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[92vw] sm:w-[clamp(360px,40vw,480px)] sm:max-w-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Rocket className="h-5 w-5 text-primary" />
            {translateInterfaceText("Iniciar Teste A/B?")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {translateInterfaceText("Você está prestes a iniciar um teste com")}{" "}{variations.length} {" "}{translateInterfaceText("variações:")}</DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-2">
          {variations.map((variation, index) => (
            <div 
              key={variation.id}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                  #{index + 1}
                </span>
                <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                  {variation.name}
                </span>
              </div>
              <span className="text-sm font-semibold text-foreground tabular-nums">
                {distribution[index] ?? 0}%
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            {translateInterfaceText("Cancelar")}</Button>
          <Button onClick={onConfirm} className="gap-2">
            <Rocket className="h-4 w-4" />
            {translateInterfaceText("Iniciar Teste")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
